<template>
  <section class="cat-pagina" :aria-labelledby="prefijo + '-titulo'">
    <div class="cat-contenido">
      <header class="cat-cabecera">
        <div>
          <h1 :id="prefijo + '-titulo'" class="cat-titulo">{{ titulo }}</h1>
          <p v-if="!error" class="cat-sub" :data-testid="prefijo + '-total'">
            <span v-if="mostrarSkeleton" class="cat-skel cat-skel-sub" aria-hidden="true"></span>
            <template v-else>{{ resumen }}</template>
          </p>
        </div>
        <div class="cat-acciones">
          <slot name="acciones"></slot>
        </div>
      </header>

      <div v-if="$slots.filtros" class="cat-filtros" :aria-label="'Filtros de ' + titulo.toLowerCase()">
        <slot name="filtros"></slot>
      </div>

      <!-- Flotante: aparecer o desaparecer no mueve la tabla -->
      <transition name="cat-aviso">
        <p v-if="aviso" class="cat-aviso" role="status" :data-testid="prefijo + '-aviso'">
          <AppIcon name="check" size="h-4 w-4" />
          <span>{{ aviso }}</span>
          <button type="button" aria-label="Cerrar aviso" @click="$emit('cerrar-aviso')">
            <AppIcon name="x-mark" size="h-4 w-4" />
          </button>
        </p>
      </transition>

      <div ref="tarjeta" class="cat-tarjeta" :style="{ '--cat-filas': limite }">
        <div
          v-if="cargando && !mostrarSkeleton"
          class="cat-progreso"
          role="progressbar"
          :aria-label="'Actualizando ' + titulo.toLowerCase()"
          :data-testid="prefijo + '-actualizando'"
        ></div>
        <div v-if="error" class="cat-error" role="alert" :data-testid="prefijo + '-error'">
          <span>{{ mensajeError }}</span>
          <button type="button" @click="$emit('reintentar')">Reintentar</button>
        </div>

        <template v-else>
          <div ref="tablaScroll" class="cat-tabla-scroll">
            <table class="cat-tabla">
              <thead ref="cabecera">
                <tr>
                  <th
                    v-for="columna in columnasVisibles"
                    :key="columna.value"
                    scope="col"
                    :class="['cat-th-' + columna.value, { 'cat-th-derecha': columna.alinear === 'derecha' }]"
                    :style="columna.ancho ? { width: columna.ancho } : null"
                    :aria-sort="columna.ordenable === false ? null : ariaSort(columna)"
                  >
                    <span v-if="columna.ordenable === false" class="cat-th-texto">{{ columna.label }}</span>
                    <button v-else type="button" class="cat-th-btn" @click="$emit('ordenar', columna.value)">
                      {{ columna.label }}
                      <AppIcon
                        v-if="ordenCampo === columna.value"
                        :name="ordenAsc ? 'chevron-up' : 'chevron-down'"
                        size="h-3.5 w-3.5"
                      />
                    </button>
                  </th>
                  <th scope="col"><span class="sr-only">Acciones</span></th>
                </tr>
              </thead>
              <tbody v-if="mostrarSkeleton" ref="cuerpo" aria-busy="true" :aria-label="'Cargando ' + titulo.toLowerCase()">
                <tr v-for="n in limite" :key="'skel-' + n" class="cat-fila-skel">
                  <td :colspan="columnasVisibles.length + 1"><div class="cat-skel"></div></td>
                </tr>
              </tbody>
              <!-- Al buscar o paginar se conservan las filas (atenuadas) hasta que llega
                   la respuesta: así la tabla no se colapsa ni brinca -->
              <tbody v-else ref="cuerpo" :class="{ 'cat-actualizando': cargando }" :aria-busy="cargando ? 'true' : null">
                <slot name="filas" :columna-visible="columnaVisible"></slot>
              </tbody>
            </table>

            <div
              v-if="!mostrarSkeleton && !total"
              class="cat-vacio"
              :class="{ 'cat-actualizando': cargando }"
              :data-testid="prefijo + '-vacio'"
            >
              <slot name="vacio"></slot>
            </div>
          </div>

          <footer v-if="mostrarSkeleton || total" ref="pie" class="cat-paginacion">
            <span v-if="mostrarSkeleton" class="cat-skel cat-skel-rango" aria-hidden="true"></span>
            <span v-else :data-testid="prefijo + '-rango'">{{ rangoInicio }}–{{ rangoFin }} de {{ total }}</span>
            <div class="cat-paginacion-botones">
              <button
                type="button"
                class="cat-icono-btn"
                aria-label="Página anterior"
                :disabled="mostrarSkeleton || pagina <= 1"
                :data-testid="prefijo + '-anterior'"
                @click="$emit('pagina', pagina - 1)"
              >
                <AppIcon name="chevron-left" size="h-4 w-4" />
              </button>
              <button
                type="button"
                class="cat-icono-btn"
                aria-label="Página siguiente"
                :disabled="mostrarSkeleton || pagina >= totalPaginas"
                :data-testid="prefijo + '-siguiente'"
                @click="$emit('pagina', pagina + 1)"
              >
                <AppIcon name="chevron-right" size="h-4 w-4" />
              </button>
            </div>
          </footer>
        </template>
      </div>
    </div>

    <!-- Modales de la pantalla -->
    <slot></slot>
  </section>
</template>

<script>
/*
 * CatalogoPagina — armazón común de las pantallas de catálogo migradas.
 *
 * Encabezado (título, resumen y acciones), barra de filtros, aviso flotante y
 * la tarjeta con la tabla: skeleton en la primera carga, filas atenuadas con
 * barra de progreso al actualizar (sin brincos), error con Reintentar, vacío
 * y paginación. La pantalla dibuja sus filas en el slot `filas` y resuelve
 * datos, filtros y modales; este componente solo presenta.
 *
 * - Mide cuántas filas caben sin scroll y lo emite en `limite` (al montar y
 *   al redimensionar) para que la pantalla pida ese tamaño de página.
 * - Columnas: { value, label, ordenable?, ancho?, alinear?: 'derecha',
 *   ocultableAngosto? }. Las `ocultableAngosto` se ocultan si la tarjeta mide
 *   menos de `anchoMinimo` en escritorio; el slot recibe `columnaVisible(valor)`.
 * - data-testid con `prefijo`: -total, -aviso, -actualizando, -error, -vacio,
 *   -rango, -anterior, -siguiente.
 */
import AppIcon from '@/components/ui/AppIcon.vue'
import { filasQueCaben } from '@/utils/paginacion'

// Móvil (tarjetas con scroll natural) y respaldo si no se puede medir
export const LIMITE_BASE = 8

export default {
  name: 'CatalogoPagina',
  components: { AppIcon },
  props: {
    titulo: { type: String, required: true },
    prefijo: { type: String, required: true },
    resumen: { type: String, default: '' },
    columnas: { type: Array, required: true },
    total: { type: Number, default: 0 },
    pagina: { type: Number, default: 1 },
    totalPaginas: { type: Number, default: 1 },
    limite: { type: Number, default: LIMITE_BASE },
    cargando: { type: Boolean, default: false },
    yaCargo: { type: Boolean, default: false },
    error: { type: Boolean, default: false },
    mensajeError: { type: String, default: 'No pudimos cargar la información.' },
    ordenCampo: { type: String, default: null },
    ordenAsc: { type: Boolean, default: true },
    aviso: { type: String, default: '' },
    anchoMinimo: { type: Number, default: 900 },
  },
  data() {
    return { angosta: false }
  },
  computed: {
    mostrarSkeleton() {
      return this.cargando && !this.yaCargo
    },
    columnasVisibles() {
      return this.angosta ? this.columnas.filter((c) => !c.ocultableAngosto) : this.columnas
    },
    rangoInicio() {
      return this.total ? (this.pagina - 1) * this.limite + 1 : 0
    },
    rangoFin() {
      return Math.min(this.pagina * this.limite, this.total)
    },
  },
  mounted() {
    this.$emit('limite', this.calcularLimite())
    window.addEventListener('resize', this.alRedimensionar)
    if (typeof window.ResizeObserver === 'function') {
      this.observadorAncho = new ResizeObserver(([entrada]) => {
        // En móvil la tabla es de tarjetas y muestra todo
        this.angosta = window.innerWidth > 700 && entrada.contentRect.width < this.anchoMinimo
      })
      this.observadorAncho.observe(this.$refs.tarjeta)
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.alRedimensionar)
    clearTimeout(this.redimensionTimer)
    if (this.observadorAncho) this.observadorAncho.disconnect()
  },
  methods: {
    columnaVisible(valor) {
      return this.columnasVisibles.some((c) => c.value === valor)
    },
    ariaSort(columna) {
      if (this.ordenCampo !== columna.value) return 'none'
      return this.ordenAsc ? 'ascending' : 'descending'
    },
    calcularLimite() {
      const escritorio =
        typeof window.matchMedia === 'function' && window.matchMedia('(min-width: 701px)').matches
      if (!escritorio || !this.$refs.tablaScroll) return LIMITE_BASE
      const fila = this.$refs.cuerpo && this.$refs.cuerpo.querySelector('tr')
      const paddingInferior = parseFloat(getComputedStyle(this.$el).paddingBottom) || 0
      return filasQueCaben({
        altoVentana: window.innerHeight,
        inicioTabla: this.$refs.tablaScroll.getBoundingClientRect().top + window.scrollY,
        altoCabecera: this.$refs.cabecera ? this.$refs.cabecera.offsetHeight : 0,
        altoFila: fila ? fila.getBoundingClientRect().height : 0,
        // Pie de paginación + borde de la tarjeta + margen inferior de la página
        altoPie: (this.$refs.pie ? this.$refs.pie.offsetHeight : 0) + 2 + paddingInferior,
      })
    },
    alRedimensionar() {
      clearTimeout(this.redimensionTimer)
      this.redimensionTimer = setTimeout(() => {
        const nuevo = this.calcularLimite()
        if (nuevo !== this.limite) this.$emit('limite', nuevo)
      }, 200)
    },
  },
}
</script>

<style scoped>
/* Mismo lenguaje visual del dashboard (Inicio.vue): Inter, tarjetas de 18px
   con borde #e4e9ef, sombras suaves, foco #7ab9f2, skeletons y error en línea.
   Las filas y los filtros los dibuja cada pantalla en slots: por eso las
   reglas llevan ::v-deep (con el mismo prefijo, para no alterar su orden). */
.cat-pagina ::v-deep .cat-tabla th.cat-th-derecha {
  text-align: right;
}
.cat-pagina {
  max-width: 100%;
  min-height: calc(100dvh - 52px);
  padding: 28px clamp(16px, 2.5vw, 36px) 48px;
  overflow-x: hidden;
  background: #f3f5f8;
  color: #0b1b2e;
  font-family: Inter, Roboto, sans-serif;
}
.cat-pagina ::v-deep .cat-contenido {
  width: 100%;
  min-width: 0;
  max-width: 1200px;
  margin: 0 auto;
}
.cat-pagina ::v-deep .sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
.cat-pagina ::v-deep .cat-cabecera {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px 18px;
  margin-bottom: 18px;
}
.cat-pagina ::v-deep .cat-titulo {
  margin: 0;
  color: #0b1b2e;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.25;
  letter-spacing: -0.015em;
}
.cat-pagina ::v-deep .cat-sub {
  margin: 4px 0 0;
  color: #607187;
  font-size: 13px;
  font-weight: 500;
}
.cat-pagina ::v-deep .cat-acciones {
  display: flex;
  min-width: 0;
  max-width: 100%;
  flex-wrap: nowrap;
  align-items: center;
  gap: 10px;
}
.cat-pagina ::v-deep .cat-filtros {
  display: flex;
  min-width: 0;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
  padding: 12px 14px;
  border: 1px solid #e4e9ef;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 3px 12px rgba(16, 42, 67, 0.035);
}
.cat-pagina ::v-deep .cat-filtro-grupo {
  display: flex;
  min-width: 0;
  align-items: center;
}
.cat-pagina ::v-deep .cat-segmentos {
  display: inline-flex;
  gap: 2px;
  padding: 4px;
  border: 1px solid #e2e8ef;
  border-radius: 13px;
  background: #f8fafc;
}
.cat-pagina ::v-deep .cat-segmentos button {
  position: relative;
  display: inline-flex;
  width: 34px;
  height: 34px;
  flex: none;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: 9px;
  background: transparent;
  color: #526273;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.16s ease, color 0.16s ease;
}
.cat-pagina ::v-deep .cat-segmentos button:hover:not(.seleccionado) {
  background: #edf3f8;
  color: #0b1b2e;
}
.cat-pagina ::v-deep .cat-segmentos button.seleccionado {
  background: #12304f;
  color: #fff;
  box-shadow: 0 2px 5px rgba(16, 42, 67, 0.17);
}
.cat-pagina ::v-deep .cat-tooltip {
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  z-index: 40;
  width: max-content;
  max-width: 150px;
  padding: 5px 7px;
  border-radius: 6px;
  background: #0b1b2e;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.2;
  opacity: 0;
  pointer-events: none;
  transform: translate(-50%, 3px);
  transition: opacity 0.14s ease, transform 0.14s ease;
}
.cat-pagina ::v-deep .cat-segmentos button:hover .cat-tooltip,
.cat-pagina ::v-deep .cat-segmentos button:focus-visible .cat-tooltip {
  opacity: 1;
  transform: translate(-50%, 0);
}
.cat-pagina ::v-deep .cat-limpiar {
  min-height: 42px;
  margin-left: auto;
  padding: 0 10px;
  border: 0;
  border-radius: 9px;
  background: transparent;
  color: #0a4f9c;
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}
.cat-pagina ::v-deep .cat-limpiar:hover {
  background: #edf3f8;
}
.cat-pagina ::v-deep .cat-segmentos button:focus-visible,
.cat-pagina ::v-deep .cat-limpiar:focus-visible {
  outline: 3px solid #7ab9f2;
  outline-offset: 2px;
}
.cat-pagina ::v-deep .cat-aviso {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 70;
  display: flex;
  max-width: min(420px, calc(100vw - 32px));
  align-items: center;
  gap: 8px;
  margin: 0;
  padding: 12px 14px;
  box-shadow: 0 12px 28px rgba(16, 42, 67, 0.16);
  border: 1px solid #cae8dc;
  border-radius: 13px;
  background: #f1faf5;
  color: #047857;
  font-size: 14px;
  font-weight: 600;
}
.cat-pagina ::v-deep .cat-aviso-enter-active,
.cat-pagina ::v-deep .cat-aviso-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}
.cat-pagina ::v-deep .cat-aviso-enter,
.cat-pagina ::v-deep .cat-aviso-leave-to {
  opacity: 0;
  transform: translateY(8px);
}
.cat-pagina ::v-deep .cat-aviso button {
  display: flex;
  margin: 0 0 0 auto;
  padding: 4px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: inherit;
  cursor: pointer;
}
.cat-pagina ::v-deep .cat-tarjeta {
  position: relative;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  border: 1px solid #e4e9ef;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 4px 15px rgba(16, 42, 67, 0.035);
}
.cat-pagina ::v-deep .cat-tabla-scroll {
  display: flex;
  max-width: 100%;
  flex-direction: column;
  overflow: hidden;
}
@media (min-width: 701px) {
  .cat-pagina {
    padding-bottom: 24px;
  }
  .cat-pagina ::v-deep .cat-tabla-scroll {
    min-height: calc(var(--cat-alto-cabecera) + var(--cat-alto-fila) * var(--cat-filas, 8));
  }
}
.cat-pagina ::v-deep .cat-tarjeta {
  --cat-alto-cabecera: 48px;
  --cat-alto-fila: 60px;
}
.cat-pagina ::v-deep .cat-progreso {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  z-index: 2;
  height: 3px;
  overflow: hidden;
  background: #e7f0fa;
}
.cat-pagina ::v-deep .cat-progreso::after {
  position: absolute;
  top: 0;
  bottom: 0;
  left: -40%;
  width: 40%;
  background: #0a4f9c;
  content: '';
  animation: cat-progreso 0.9s ease-in-out infinite;
}
@keyframes cat-progreso {
  to {
    left: 100%;
  }
}
.cat-pagina ::v-deep .cat-actualizando {
  opacity: 0.55;
  transition: opacity 0.15s ease;
}
.cat-pagina ::v-deep .cat-tabla tbody,
.cat-pagina ::v-deep .cat-vacio {
  transition: opacity 0.15s ease;
}
.cat-pagina ::v-deep .cat-tabla tr.cat-fila-skel td {
  height: var(--cat-alto-fila);
  box-sizing: border-box;
}
@media (min-width: 701px) {
  .cat-pagina ::v-deep .cat-tabla tbody tr td {
    height: var(--cat-alto-fila);
    box-sizing: border-box;
  }
}
.cat-pagina ::v-deep .cat-skel.cat-skel-sub {
  display: inline-block;
  width: 96px;
  height: 12px;
  vertical-align: middle;
}
.cat-pagina ::v-deep .cat-skel.cat-skel-rango {
  display: inline-block;
  width: 80px;
  height: 12px;
}
@media (prefers-reduced-motion: reduce) {
  .cat-pagina ::v-deep .cat-progreso::after {
    animation: none;
    left: 0;
    width: 100%;
    opacity: 0.6;
  }
}
.cat-pagina ::v-deep .cat-tabla {
  width: 100%;
  min-width: 0;
  table-layout: fixed;
  border-collapse: collapse;
}
.cat-pagina ::v-deep .cat-tabla th:last-child {
 width: 108px; 
}
.cat-pagina ::v-deep .cat-tabla th {
  padding: 12px 16px;
  border-bottom: 1px solid #e4e9ef;
  background: #f8fafc;
  color: #3d4b5f;
  font-size: 13px;
  font-weight: 700;
  text-align: left;
  white-space: nowrap;
}
.cat-pagina ::v-deep .cat-th-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 4px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: inherit;
  font: inherit;
  cursor: pointer;
  transition: color 0.16s ease, background 0.16s ease;
}
.cat-pagina ::v-deep .cat-th-texto {
  padding: 2px 4px;
}
.cat-pagina ::v-deep .cat-th-btn:hover {
  background: #edf3f8;
  color: #0a4f9c;
}
.cat-pagina ::v-deep .cat-tabla td {
  overflow: hidden;
  padding: 10px 16px;
  border-bottom: 1px solid #eef2f6;
  color: #0b1b2e;
  font-size: 14px;
  vertical-align: middle;
  text-overflow: ellipsis;
}
.cat-pagina ::v-deep .cat-tabla tbody tr {
  transition: background 0.16s ease;
}
.cat-pagina ::v-deep .cat-tabla tbody tr:hover {
  background: #f8fafc;
}
.cat-pagina ::v-deep .cat-tabla tbody tr:last-child td {
  border-bottom: 0;
}
.cat-pagina ::v-deep .cat-nombre {
  display: flex;
  align-items: center;
  gap: 10px;
}
.cat-pagina ::v-deep .cat-nombre-datos,
.cat-pagina ::v-deep .cat-pila {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 3px;
}
.cat-pagina ::v-deep .cat-avatar {
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: #eaf2fb;
  color: #0a4f9c;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.02em;
}
.cat-pagina ::v-deep .cat-nombre-texto {
  overflow: hidden;
  font-weight: 600;
  color: #102a43;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cat-pagina ::v-deep .cat-meta {
  overflow: hidden;
  color: #607187;
  font-size: 12px;
  line-height: 1.25;
  /* Una sola línea: todas las filas miden igual y el cálculo de filas es exacto */
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cat-pagina ::v-deep .cat-sin-dato {
  color: #b3bfcc;
}
.cat-pagina ::v-deep .cat-tabla td.cat-fila-acciones {
  overflow: visible;
  padding-right: 12px;
  padding-left: 8px;
  text-align: right;
  text-overflow: clip;
  white-space: nowrap;
}
.cat-pagina ::v-deep .cat-icono-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  margin: 0 0 0 6px;
  padding: 0;
  border: 1px solid #d8e0ea;
  border-radius: 10px;
  background: #fff;
  color: #3d4b5f;
  cursor: pointer;
  transition: background 0.16s ease, color 0.16s ease, border-color 0.16s ease;
}
.cat-pagina ::v-deep .cat-icono-btn:hover:not(:disabled) {
  border-color: #0a4f9c;
  background: #edf3f8;
  color: #0a4f9c;
}
.cat-pagina ::v-deep .cat-icono-btn.peligro:hover:not(:disabled) {
  border-color: #b91c1c;
  background: #fdf2f2;
  color: #b91c1c;
}
.cat-pagina ::v-deep .cat-icono-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.cat-pagina ::v-deep .cat-icono-btn:focus-visible,
.cat-pagina ::v-deep .cat-th-btn:focus-visible,
.cat-pagina ::v-deep .cat-aviso button:focus-visible {
  outline: 3px solid #7ab9f2;
  outline-offset: 2px;
}
.cat-pagina ::v-deep .cat-skel {
  height: 16px;
  border-radius: 6px;
  background: linear-gradient(100deg, #e6ebf1 20%, #f5f7fa 40%, #e6ebf1 60%);
  background-size: 200% 100%;
  animation: cat-skel 1.4s ease-in-out infinite;
}
@keyframes cat-skel {
  to {
    background-position-x: -200%;
  }
}
.cat-pagina ::v-deep .cat-vacio {
  display: flex;
  flex: 1;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 48px 20px;
  color: #607187;
  font-size: 14px;
  text-align: center;
}
.cat-pagina ::v-deep .cat-vacio svg {
  color: #758ca2;
}
.cat-pagina ::v-deep .cat-vacio p {
  margin: 0;
}
.cat-paginacion {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  border-top: 1px solid #e4e9ef;
  color: #607187;
  font-size: 13px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
}
.cat-paginacion-botones {
  display: inline-flex;
  gap: 2px;
  padding: 4px;
  border: 1px solid #e2e8ef;
  border-radius: 13px;
  background: #fff;
  box-shadow: 0 3px 12px rgba(16, 42, 67, 0.04);
}
.cat-pagina ::v-deep .cat-paginacion-botones .cat-icono-btn {
  width: 38px;
  height: 34px;
  margin: 0;
  border: 0;
  border-radius: 9px;
  background: transparent;
}
.cat-pagina ::v-deep .cat-paginacion-botones .cat-icono-btn:hover:not(:disabled) {
  border: 0;
  background: #edf3f8;
  color: #0b1b2e;
}
.cat-pagina ::v-deep .cat-error {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 80px;
  padding: 18px 20px;
  background: #fff8f8;
  color: #8d232d;
  font-size: 14px;
}
.cat-pagina ::v-deep .cat-error button {
  flex: none;
  padding: 9px 13px;
  border: 1px solid #b91c1c;
  border-radius: 8px;
  background: #fff;
  color: #a3212b;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
}
.cat-pagina ::v-deep .cat-error button:focus-visible {
  outline: 3px solid #7ab9f2;
  outline-offset: 2px;
}
.cat-pagina ::v-deep .cat-confirmacion {
  margin: 0;
  color: #3d4b5f;
  font-size: 14px;
  line-height: 1.5;
}
@media (max-width: 639px) {
  .cat-pagina ::v-deep .cat-aviso {
    right: 12px;
    bottom: 12px;
    left: 12px;
  }
  .cat-pagina ::v-deep .cat-cabecera {
    flex-direction: column;
    align-items: stretch;
  }
  .cat-pagina ::v-deep .cat-acciones {
    justify-content: flex-end;
  }
  .cat-paginacion {
    flex-direction: column-reverse;
    justify-content: center;
    gap: 8px;
  }
}
@media (max-width: 899px) {
  .cat-pagina ::v-deep .cat-filtros {
    align-items: center;
  }
  .cat-pagina ::v-deep .cat-limpiar {
    margin-left: 0;
  }
}
@media (max-width: 700px) {
  .cat-pagina {
    padding-right: 12px;
    padding-left: 12px;
  }
  .cat-pagina ::v-deep .cat-filtros {
    padding: 10px;
  }
  .cat-pagina ::v-deep .cat-tabla {
    min-width: 0;
  }
  .cat-pagina ::v-deep .cat-tabla thead {
    display: none;
  }
  .cat-pagina ::v-deep .cat-tabla,
  .cat-pagina ::v-deep .cat-tabla tbody {
    display: block;
  }
  .cat-pagina ::v-deep .cat-tabla tbody tr {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    padding: 14px;
    border-bottom: 1px solid #e4e9ef;
  }
  .cat-pagina ::v-deep .cat-tabla tbody tr:last-child {
    border-bottom: 0;
  }
  .cat-pagina ::v-deep .cat-tabla td {
    display: block;
    grid-column: 1;
    padding: 4px 0;
    border: 0;
  }
  .cat-pagina ::v-deep .cat-tabla td.cat-fila-acciones {
    display: flex;
    grid-column: 2;
    grid-row: 1 / span 4;
    align-items: flex-start;
    padding: 0 0 0 10px;
  }
  .cat-pagina ::v-deep .cat-skel {
    width: calc(100vw - 58px);
  }
}

@media (pointer: coarse), (max-width: 1023px) {
  .cat-pagina ::v-deep .cat-tarjeta { --cat-alto-fila: 68px; }
  .cat-pagina ::v-deep .cat-segmentos button,
  .cat-pagina ::v-deep .cat-icono-btn,
  .cat-pagina ::v-deep .cat-paginacion-botones .cat-icono-btn { width: 44px; height: 44px; }
  .cat-pagina ::v-deep .cat-tabla th:last-child { width: 124px; }
  .cat-pagina ::v-deep .cat-th-btn { min-height: 44px; }
}
</style>
